import styled from '@emotion/styled';
import { captureException, captureEvent, withScope } from '@sentry/browser';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useHistory, useLocation } from 'react-router';
import queryString from 'query-string';

import { Dispatch } from 'src/config/rootStore';
import { getUserInformation } from 'src/modules/user/selectors/userSelectors';
import FallbackCard from 'src/modules/dashboard/components/FallbackCard';
import { getMyProviders } from 'src/modules/providers/selectors/providersSelectors';
import { DailyPauseCard } from 'src/modules/scheduledContent/components/DailyPauseCard';
import CrisisSupportBanner from 'src/shared/components/CrisisSupportBanner';
import { layout } from '@modernhealth/vital-foundation';

import {
    ActionItemList,
    ActionItemListSkeleton,
    ActionItemSkeleton,
    ActionPropsWithId,
    ContentBannerSkeleton,
    ContentCardsRowSkeleton,
    PageHeader,
    PageHeaderSkeleton,
    SectionHeaderSkeleton,
    Grid,
    GridItem,
    Modal,
    pxToRem,
} from '@modernhealth/vital-components';

import { useLocalizedVersion } from 'src/shared/hooks/useLocalizedVersion';
import { RenderScreenSectionNode } from 'src/modules/dashboard/components/ContentCard';
import { ForYouModal } from 'src/modules/dashboard/components/ForYouModal';
import { mapContentLinkToActionItemProps } from 'src/modules/dashboard/helpers/contentLinkNodeToActionItem';
import { appClient } from 'src/config/apollo';
import { delay } from 'src/shared/helpers/delay';
import { toastNotificationsService } from 'src/config/rootService';
import { config } from 'src/config/config';
import { getUpcomingVideoSessions } from 'src/modules/videoSessions/selectors/videoSessionsSelectors';
import { CoachingVisitNode } from 'src/generated/webapp_gql';
import { SessionDetails } from 'src/modules/videoSessions/components/SessionDetails';
import { getFeaturedBenefitsAndCategories } from 'src/modules/benefits/selectors/benefitsSelectors';
import { TimeZoneModal } from 'src/modules/user/components/TimeZoneModal';

import { ScreenSectionFragment, useGetHomeScreenQuery } from '../documents/getHomeScreen.generated';
import { ChatInfoMobileSendLinkModal } from '../components/ChatInfoMobileSendLinkModal';
import { ChatInfoMobileResendLinkModal } from '../components/ChatInfoMobileResendLinkModal';
import { useDownloadAppMutation } from '../documents/downloadAppMutation.generated';
import { BenefitBannerCard } from 'src/modules/benefits/components/BenefitBanner';
import { HomePageTestID } from './testIds';
import { ErrorFallback } from '../components/ErrorFallback';
import { useAnalytics } from '@modernhealth/web-analytics';

const Wrapper = styled.div({
    width: '100%',
    maxWidth: layout.containerWidth,
    margin: '0 auto',
    paddingLeft: layout.containerPaddingX,
    paddingRight: layout.containerPaddingX,
    paddingBottom: layout.footerHeight,
});

interface LocationProps {
    action?: 'chat' | 'view_session' | 'foryou';
    sessionId?: string;
}

export const HomePage: React.FC = () => {
    const { timeZoneModal = true, showCirclesInMemberWeb } = useFlags();

    const { t } = useTranslation();
    const user = useSelector(getUserInformation);
    const myProviders = useSelector(getMyProviders);
    const [localizedVersion] = useLocalizedVersion();
    const {
        showDailyPauseOnWeb,
        dailyPauseStreaksExperimentVariant,
        startDailyPauseStreaksExperimentWeb,
    } = useFlags();
    const [downloadAppMutation] = useDownloadAppMutation({
        client: appClient,
    });
    const history = useHistory<LocationProps>();
    const dispatch = useDispatch<Dispatch>();
    const { trackClick } = useAnalytics();

    const [isForYouModalVisible, setIsForYouModalVisible] = useState(false);
    const [sendLinkModalVisible, setSendLinkModalVisible] = useState(false);
    const [resendLinkModalVisible, setResendLinkModalVisible] = useState(false);
    const { state, search } = useLocation<LocationProps>();
    const [selectedSession, setSelectedSession] = useState<CoachingVisitNode | null>(null);
    const { benefitCategories, featuredBenefits } = useSelector(getFeaturedBenefitsAndCategories);
    const shouldShowBenefitBanner = benefitCategories.length > 0 || featuredBenefits.length > 0;

    const upcomingSessions = useSelector(getUpcomingVideoSessions);

    const showStreaksInfo =
        startDailyPauseStreaksExperimentWeb && dailyPauseStreaksExperimentVariant === 'treatment';

    const {
        data,
        loading: homeScreenLoading,
        error,
        refetch,
    } = useGetHomeScreenQuery({
        errorPolicy: 'all',
        fetchPolicy: 'network-only',
        variables: {
            fetchScheduledContentActivity: showStreaksInfo,
        },
    });

    const homeScreenData = data?.homeScreen;

    useEffect(() => {
        const query = queryString.parse(search);

        if (typeof query.action === 'string') {
            switch (query.action) {
                case 'foryou':
                    history.replace('/dashboard', { action: 'foryou' });
                    return;
                case 'view_session':
                    if (typeof query.session_id == 'string') {
                        history.replace('/dashboard', {
                            action: 'view_session',
                            sessionId: query.session_id,
                        });
                    } else {
                        captureEvent({
                            message:
                                'Attempted to navigate to "view_session" action without a provided session ID.',
                        });
                    }
                    return;
                case 'chat':
                    history.replace('/dashboard', { action: 'chat' });
                    return;
            }
        }
    }, [search]);

    useEffect(() => {
        if (!state || !state.action) {
            return;
        }
        if (state.action === 'chat') {
            setSendLinkModalVisible(true);
            return;
        }

        if (state.action === 'view_session' && state.sessionId) {
            const session = upcomingSessions.find(session => session.uuid === state.sessionId);
            setSelectedSession(session ?? null);
            return;
        }

        if (state.action === 'foryou') {
            setIsForYouModalVisible(true);
            return;
        }
    }, [state, upcomingSessions]);

    useEffect(() => {
        if (error) {
            captureException(error);
        }
    }, [error]);

    useEffect(() => {
        if (!homeScreenLoading && !error && !data?.homeScreen) {
            withScope(scope => {
                scope.setExtra('response', JSON.stringify(data));

                captureException(
                    new Error('Missing required data in the response to render home page'),
                );
            });
        }
    }, [data, homeScreenLoading, error]);

    useEffect(() => {
        dispatch.user.fetchUserInfo();
    }, []);

    const handleSendLink = async () => {
        try {
            await downloadAppMutation();

            if (!resendLinkModalVisible) {
                setSendLinkModalVisible(false);
                await delay(300);
                setResendLinkModalVisible(true);
            } else {
                setResendLinkModalVisible(false);
            }
        } catch (error) {
            toastNotificationsService.renderErrorToast(config.messages.genericError);
            captureException(error);
        }
    };

    if (homeScreenLoading && !homeScreenData) {
        return (
            <Wrapper>
                <PageHeaderSkeleton />
                <Grid>
                    <GridItem lg={3} md={2}>
                        <ContentBannerSkeleton />
                    </GridItem>
                    <GridItem lg={2} md={2}>
                        <ActionItemListSkeleton actions={[{}, {}, {}]} showSecondaryButton />
                    </GridItem>
                </Grid>
                <SectionHeaderSkeleton showSecondaryButton />
                <ContentCardsRowSkeleton items={[{}, {}, {}, {}, {}]} />
                <SectionHeaderSkeleton showSecondaryButton />
                <Grid lgMode="3columns">
                    <GridItem lg={1} md={2}>
                        <ActionItemListSkeleton actions={[{}, {}, {}]} showSecondaryButton />
                    </GridItem>
                    <GridItem lg={1} md={2} hide="sm">
                        <ActionItemListSkeleton actions={[{}, {}, {}]} showSecondaryButton />
                    </GridItem>
                    <GridItem lg={1} md={2} hide="md">
                        <ActionItemListSkeleton actions={[{}, {}, {}]} showSecondaryButton />
                    </GridItem>
                </Grid>
                <SectionHeaderSkeleton showSecondaryButton={false} />
                <Grid lgMode="3columns">
                    <GridItem lg={1} md={2}>
                        <ActionItemSkeleton hideIcon hideDescription />
                    </GridItem>
                </Grid>
            </Wrapper>
        );
    }

    if (!homeScreenData) {
        return <ErrorFallback />;
    }

    const handleViewAllTextPress = async () => {
        await dispatch.videoSessions.fetchSessions(); // update the redux store session status to match BE - race condition
        setIsForYouModalVisible(true);
        trackClick('view all', {
            page_section_uuid: homeScreenData.featuredSection.uuid,
            page_section: homeScreenData.featuredSection.title,
            button_copy: 'View all',
        });
    };

    const sections = homeScreenData.screenSections;
    const scheduledContent = homeScreenData.scheduledContent;

    const forYouActions = homeScreenData.featuredSection.contentCollectionLinks
        .map((link, contentIndex) =>
            mapContentLinkToActionItemProps({
                link,
                showCirclesInMemberWeb,
                tracking: {
                    props: {
                        page_section: homeScreenData.featuredSection.title,
                        page_section_uuid: homeScreenData.featuredSection.uuid,
                    },
                    contentIndex,
                    trackClick,
                },
            }),
        )
        .filter((link): link is ActionPropsWithId => link !== null);

    return (
        <Wrapper data-testid={HomePageTestID.Wrapper}>
            <PageHeader>{t('dashboard:hello', { firstName: user.firstName })}</PageHeader>
            <Grid>
                <GridItem lg={3} md={2}>
                    {!showDailyPauseOnWeb || localizedVersion || !scheduledContent ? (
                        <FallbackCard providers={myProviders} />
                    ) : (
                        <DailyPauseCard
                            scheduledContent={scheduledContent}
                            dayStreakCount={data?.scheduledContentActivity?.dayStreakCount ?? 0}
                            weekStreakCount={data?.scheduledContentActivity?.weekStreakCount ?? 0}
                            showStreakInfo={showStreaksInfo}
                        />
                    )}
                </GridItem>
                <GridItem data-testid={HomePageTestID.ForYouSection} lg={2} md={2}>
                    <ActionItemList
                        title={homeScreenData.featuredSection.title}
                        actions={forYouActions}
                        secondaryText={t('shared:view_all')}
                        onSecondaryTextPress={handleViewAllTextPress}
                    />
                </GridItem>
            </Grid>
            {sections
                .filter((section): section is ScreenSectionFragment => section !== null)
                .map(section => (
                    <RenderScreenSectionNode section={section} key={section.id} />
                ))}
            {shouldShowBenefitBanner && (
                <BenefitBannerCard companyName={user.company}></BenefitBannerCard>
            )}
            <CrisisContainer>
                <CrisisSupportBanner useDSComponents />
            </CrisisContainer>
            <ForYouModal
                handleClose={() => setIsForYouModalVisible(false)}
                forYouActions={forYouActions}
                isVisible={isForYouModalVisible}
            />
            <ChatInfoMobileSendLinkModal
                visible={sendLinkModalVisible}
                onSendLinkClick={handleSendLink}
                onClose={() => {
                    setSendLinkModalVisible(false);
                }}
            />
            <ChatInfoMobileResendLinkModal
                visible={resendLinkModalVisible}
                onSendLinkClick={handleSendLink}
                onClose={() => {
                    setResendLinkModalVisible(false);
                }}
            />
            <ModalContainer>
                <Modal
                    maxWidth={560}
                    onCloseButtonClick={() => {
                        setSelectedSession(null);
                    }}
                    isVisible={!!selectedSession}
                >
                    {selectedSession && (
                        <SessionDetails
                            session={selectedSession}
                            onClose={() => {
                                setSelectedSession(null);
                                refetch();
                            }}
                        />
                    )}
                </Modal>
            </ModalContainer>
            {timeZoneModal && <TimeZoneModal />}
        </Wrapper>
    );
};

const ModalContainer = styled.div({
    'div[class*="content"]': {
        borderRadius: 0,
    },
});

const CrisisContainer = styled.div({
    marginTop: pxToRem(20),
    display: 'flex',
    justifyContent: 'center',
});
